import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Settings, User, Lock, Bell, Check, AlertCircle, Loader2 } from 'lucide-react';
import { trackDashboard, trackPageView } from '@/services/analytics';
import { useScrollTracking } from '@/hooks/useAnalytics';
import { accountService, NotificationPreferences } from '@/services/account.service';
import { setUser } from '@/redux/actions/user';

interface ToastState {
  show: boolean;
  message: string;
  type: 'success' | 'error';
}

const DashboardSettings: React.FC = () => {
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.user.user);
  const [activeTab, setActiveTab] = useState('profile');

  // Profile state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);

  // Security state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [securityLoading, setSecurityLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  // Notifications state
  const [notifications, setNotifications] = useState<NotificationPreferences>({
    course_updates: true,
    marketing_emails: false,
    weekly_progress_report: true
  });
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationsInitialLoading, setNotificationsInitialLoading] = useState(true);

  // Toast state
  const [toast, setToast] = useState<ToastState>({ show: false, message: '', type: 'success' });

  useScrollTracking('DashboardSettings');

  useEffect(() => {
    trackDashboard.view('settings');
    trackPageView('DashboardSettings');
  }, []);

  // Initialize profile data from user
  useEffect(() => {
    if (user) {
      setFirstName(user.first_name || '');
      setLastName(user.last_name || '');
      setEmail(user.email || '');
    }
  }, [user]);

  // Load notification preferences
  useEffect(() => {
    const loadNotificationPreferences = async () => {
      setNotificationsInitialLoading(true);
      const result = await accountService.getNotificationPreferences();
      if (result.success && result.data) {
        setNotifications(result.data);
      }
      setNotificationsInitialLoading(false);
    };
    loadNotificationPreferences();
  }, []);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 4000);
  };

  const handleProfileSave = async () => {
    setProfileLoading(true);
    const result = await accountService.updateProfile({
      first_name: firstName,
      last_name: lastName,
      email: email
    });

    if (result.success && result.data) {
      // Update Redux store
      dispatch(setUser({
        ...user,
        first_name: result.data.first_name,
        last_name: result.data.last_name,
        email: result.data.email
      }) as any);
      showToast('Profile updated successfully', 'success');
    } else {
      showToast(result.error || 'Failed to update profile', 'error');
    }
    setProfileLoading(false);
  };

  const handlePasswordChange = async () => {
    // Validation
    setPasswordError('');

    if (!currentPassword) {
      setPasswordError('Current password is required');
      return;
    }

    if (!newPassword) {
      setPasswordError('New password is required');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    setSecurityLoading(true);
    const result = await accountService.changePassword({
      current_password: currentPassword,
      new_password: newPassword
    });

    if (result.success) {
      showToast('Password changed successfully', 'success');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } else {
      setPasswordError(result.error || 'Failed to change password');
    }
    setSecurityLoading(false);
  };

  const handleNotificationsSave = async () => {
    setNotificationsLoading(true);
    const result = await accountService.updateNotificationPreferences(notifications);

    if (result.success) {
      // Update Redux store with notification preferences
      dispatch(setUser({
        ...user,
        notification_preferences: notifications
      }) as any);
      showToast('Notification preferences saved', 'success');
    } else {
      showToast(result.error || 'Failed to save preferences', 'error');
    }
    setNotificationsLoading(false);
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: <User className="w-4 h-4" /> },
    { id: 'security', label: 'Security', icon: <Lock className="w-4 h-4" /> },
    { id: 'notifications', label: 'Notifications', icon: <Bell className="w-4 h-4" /> },
  ];

  return (
    <div className="max-w-5xl mx-auto">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`fixed top-24 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg transition-all ${
          toast.type === 'success'
            ? 'bg-green-600 text-white'
            : 'bg-red-600 text-white'
        }`}>
          {toast.type === 'success' ? (
            <Check className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-white mb-2 flex items-center gap-3">
          <Settings className="w-10 h-10" />
          Account Settings
        </h1>
        <p className="text-gray-300 text-lg">
          Manage your account preferences and security
        </p>
      </div>

      {/* Tabs */}
      <div className="bg-slate-800/60 backdrop-blur-sm border-2 border-indigo-500/30 rounded-2xl overflow-hidden">
        <div className="flex border-b border-white/10">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-slate-800/40'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-8">
          {activeTab === 'profile' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white mb-6">Profile Information</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-900/50 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter your first name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-900/50 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter your last name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter your email"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Username
                </label>
                <input
                  type="text"
                  value={user?.username || ''}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-white/10 rounded-lg text-gray-500 cursor-not-allowed"
                  placeholder="Your username"
                  disabled
                />
                <p className="text-xs text-gray-500 mt-1">Username cannot be changed</p>
              </div>

              <button
                onClick={handleProfileSave}
                disabled={profileLoading}
                className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:shadow-lg hover:shadow-indigo-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {profileLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                {profileLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white mb-6">Security Settings</h2>

              {/* Reset Password Info Box */}
              <div className="p-4 bg-slate-900/50 border border-indigo-500/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <Lock className="w-5 h-5 text-indigo-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="text-white font-medium mb-1">Reset Your Password</h3>
                    <p className="text-sm text-gray-400">
                      Use the form below to change your account password. You'll need to enter your current password for verification, then choose a new secure password.
                    </p>
                  </div>
                </div>
              </div>

              {passwordError && (
                <div className="flex items-center gap-2 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400">
                  <AlertCircle className="w-5 h-5" />
                  {passwordError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Current Password
                </label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter current password"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  New Password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter new password (min 8 characters)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Confirm new password"
                />
              </div>

              <button
                onClick={handlePasswordChange}
                disabled={securityLoading}
                className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:shadow-lg hover:shadow-indigo-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {securityLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                {securityLoading ? 'Updating...' : 'Update Password'}
              </button>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white mb-6">Notification Preferences</h2>

              {notificationsInitialLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
                </div>
              ) : (
                <>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-slate-900/50 border border-white/10 rounded-lg">
                      <div>
                        <h3 className="text-white font-medium">Course Updates</h3>
                        <p className="text-sm text-gray-400">Receive updates about your enrolled courses</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={notifications.course_updates}
                          onChange={(e) => setNotifications({...notifications, course_updates: e.target.checked})}
                        />
                        <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-slate-900/50 border border-white/10 rounded-lg">
                      <div>
                        <h3 className="text-white font-medium">Marketing Emails</h3>
                        <p className="text-sm text-gray-400">Receive promotional content and special offers</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={notifications.marketing_emails}
                          onChange={(e) => setNotifications({...notifications, marketing_emails: e.target.checked})}
                        />
                        <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-slate-900/50 border border-white/10 rounded-lg">
                      <div>
                        <h3 className="text-white font-medium">Weekly Progress Report</h3>
                        <p className="text-sm text-gray-400">Get a weekly summary of your learning progress</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={notifications.weekly_progress_report}
                          onChange={(e) => setNotifications({...notifications, weekly_progress_report: e.target.checked})}
                        />
                        <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                      </label>
                    </div>
                  </div>

                  <button
                    onClick={handleNotificationsSave}
                    disabled={notificationsLoading}
                    className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:shadow-lg hover:shadow-indigo-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {notificationsLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                    {notificationsLoading ? 'Saving...' : 'Save Preferences'}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardSettings;
